import LoadingQuestion from 'components/LoadingComponents/LoadingQuestion';
import Choice from 'containers/NewQuestionnaire/components/Choice';
import {Button} from 'letrus-ui';
import {useEffect, useState} from 'react';
import {
  EnrichedQuestion,
  UniqueChoiceFeedback,
} from 'utils/types/questionnaires';
import {QuestionSubmitParams} from 'containers/PracticeTest';
import styles from './RadioQuestion.module.scss';

function getChoiceCorrectness(
  feedback: UniqueChoiceFeedback | undefined,
  choiceId: number,
): boolean | undefined {
  if (feedback?.correctItem === choiceId) return true;
  if (feedback?.incorrectItem === choiceId) return false;
  return undefined;
}

export interface RadioQuestionProps {
  question: EnrichedQuestion;
  onGoBack(): void;
  goToNextQuestion(): void;
  onQuestionSubmit(params: QuestionSubmitParams): void;
  isLoading: boolean;
  isQuestionnaireFinished: boolean;
  answersVisible?: boolean;
}

function RadioQuestion({
  question,
  onGoBack,
  onQuestionSubmit,
  goToNextQuestion,
  isLoading,
  isQuestionnaireFinished,
  answersVisible = false,
}: RadioQuestionProps): JSX.Element {
  const [selectedItem, setSelectedItem] = useState<number>();
  const [feedback, setFeedBack] = useState<UniqueChoiceFeedback>();
  const {selectedChoice, correctChoice, isCorrect} = question.responses;

  useEffect(() => {
    setSelectedItem(selectedChoice);
  }, [question]);

  useEffect(() => {
    if (answersVisible) {
      if (!question.has_correct_answer) {
        setFeedBack({showChoiceFeedback: true});
      } else {
        setFeedBack({
          correctItem: correctChoice,
          incorrectItem: !isCorrect ? selectedChoice : undefined,
          generalFeedback: isCorrect
            ? question.correct_feedback
            : question.incorrect_feedback,
          showCorrectAnswer: !isCorrect,
        });
      }
    }
  }, [question, answersVisible]);

  function handleSubmit() {
    onQuestionSubmit({
      questionId: question.id,
      responses: [
        {
          feedback: feedback?.generalFeedback as string,
          id: question.id,
          is_response_correct: false,
          option: selectedItem!,
        },
      ],
    });

    goToNextQuestion();
  }

  // `hasBeenAnswered` here is about showing feedback/color, not about blocking selection — the
  // name is inherited from `Choice` (NewQuestionnaire) to reuse the same component without
  // duplicating the correct/incorrect state logic (found by the user, 2026-09-22: SAEB and
  // Trilha used completely different selection and button components).
  const hasBeenAnswered = Boolean(feedback);

  return isLoading ? (
    <LoadingQuestion questionType="radio" />
  ) : (
    <article className={styles.wrapper} aria-live="polite">
      <div className={styles.header}>
        <h1>{question.name}</h1>
        {/* eslint-disable-next-line react/no-danger */}
        <div dangerouslySetInnerHTML={{__html: question.text || ''}} />
      </div>

      <fieldset className={styles.options} disabled={isQuestionnaireFinished}>
        <legend className={styles.srOnly}>Opções</legend>
        {question.choices?.map((choice) => (
          <Choice
            key={choice.id}
            name={`radio-question-${question.id}`}
            value={choice.id}
            text={choice.name}
            type="radio"
            checked={selectedItem === choice.id}
            disabled={isQuestionnaireFinished}
            isCorrect={getChoiceCorrectness(feedback, choice.id)}
            hasBeenAnswered={hasBeenAnswered}
            hasCorrectAnswer={question.has_correct_answer}
            feedback={
              feedback?.showChoiceFeedback && selectedItem === choice.id
                ? choice.feedback
                : undefined
            }
            onChange={() =>
              !isQuestionnaireFinished && setSelectedItem(choice.id)
            }
          />
        ))}
      </fieldset>

      {feedback?.generalFeedback && (
        <span className={styles.feedback}>{feedback.generalFeedback}</span>
      )}

      <div className={styles.controls}>
        <Button
          type="button"
          variant="secondary"
          onClick={onGoBack}
          disabled={question.order === 1}
        >
          Voltar
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSubmit}
          disabled={!selectedItem}
          data-testid="submit-button"
        >
          {question.button_text || 'Próxima'}
        </Button>
      </div>
    </article>
  );
}

export default RadioQuestion;
